/**
 * Free WhatsApp click-to-chat helpers.
 * No API fees, no Meta verification — just builds `wa.me/…?text=…` links
 * that open WhatsApp Business on the phone with the message pre-filled.
 * The admin taps Send. Zero infrastructure.
 */

/**
 * Normalise a South African cellphone number to `wa.me` format
 * (international, digits only). Examples:
 *   "081 506 9375"  → "27815069375"
 *   "0815069375"    → "27815069375"
 *   "+27 81 506..." → "27815069375"
 *   "27815069375"   → "27815069375"
 * Returns null if we can't confidently normalise (missing / gibberish).
 */
export const normalizeZaPhone = (raw) => {
  if (!raw) return null;
  const digits = String(raw).replace(/[^\d]/g, "");
  if (!digits) return null;
  // Already ZA international
  if (digits.startsWith("27") && digits.length >= 11) return digits;
  // Local ZA — strip leading 0, prefix 27
  if (digits.startsWith("0") && digits.length === 10) return "27" + digits.slice(1);
  // Bare 9-digit ZA (missing leading 0)
  if (digits.length === 9) return "27" + digits;
  // Fallback — return as-is if it looks reasonable (10+ digits)
  return digits.length >= 10 ? digits : null;
};

/**
 * Build a `wa.me` URL. Pass phone as any ZA format (or null → generic link).
 */
export const buildWhatsAppLink = (phone, message = "") => {
  const num = normalizeZaPhone(phone);
  const base = num ? `https://wa.me/${num}` : "https://wa.me/";
  return `${base}?text=${encodeURIComponent(message)}`;
};

/**
 * Open WhatsApp Business (mobile) or WhatsApp Web (desktop) with the
 * message pre-filled. Returns the URL so callers can also copy it if the
 * popup is blocked.
 */
export const openWhatsApp = (phone, message = "") => {
  const url = buildWhatsAppLink(phone, message);
  try { window.open(url, "_blank", "noopener,noreferrer"); } catch { /* ignore */ }
  return url;
};

// -----------------------------------------------------------------------------
// Templates — short, punchy, WhatsApp-native voice. Portal link goes in each.
// -----------------------------------------------------------------------------
const COMPANY = "Diesel & Dust Windscreens";
const first = (fullName) => (fullName || "").trim().split(/\s+/)[0] || "there";

export const waTemplates = {
  status_scheduled: ({ client_name, job_number, fitment_date, fitment_time, fitment_address, portal_url }) =>
    `Hi ${first(client_name)}, your fitment is booked for ${fitment_date || "TBC"} ${fitment_time || ""}` +
    (fitment_address ? ` at ${fitment_address}` : "") +
    `.\nJob #${job_number} · ${COMPANY}` +
    (portal_url ? `\nDetails: ${portal_url}` : ""),

  status_en_route: ({ client_name, job_number, portal_url }) =>
    `Hi ${first(client_name)}, our fitter is on the way to you now 🚗\nJob #${job_number} · ${COMPANY}` +
    (portal_url ? `\nDetails: ${portal_url}` : ""),

  status_on_site: ({ client_name, job_number }) =>
    `Hi ${first(client_name)}, our fitter has arrived and is starting the pre-fitment inspection.\nJob #${job_number} · ${COMPANY}`,

  status_completed: ({ client_name, job_number, portal_url }) =>
    `Hi ${first(client_name)}, the fitment is complete ✅. Please allow 120 mins (240 with airbags) for the windscreen to bond before driving.\n` +
    `Job #${job_number} · ${COMPANY}` +
    (portal_url ? `\nDocuments: ${portal_url}` : ""),

  status_invoiced: ({ client_name, tax_invoice_number, portal_url }) =>
    `Hi ${first(client_name)}, your tax invoice ${tax_invoice_number || ""} is ready.\n${COMPANY}` +
    (portal_url ? `\nDownload: ${portal_url}` : ""),

  documents_ready: ({ client_name, job_number, portal_url }) =>
    `Hi ${first(client_name)}, all documents for Job #${job_number} are ready to download:\n${portal_url || ""}\n${COMPANY}`,

  quote_ready: ({ client_name, quote_number, portal_url }) =>
    `Hi ${first(client_name)}, we've prepared quote ${quote_number || ""} for you.\n` +
    `Approve/decline here: ${portal_url || ""}\n${COMPANY}`,

  insurer_bundle: ({ job_number, client_name, vehicle, claim_number, portal_url }) =>
    `Good day, please find the claim documents for Job #${job_number} — ${client_name || "—"}` +
    (vehicle ? ` (${vehicle})` : "") +
    (claim_number ? `, Claim ${claim_number}` : "") +
    `.\nDownload: ${portal_url || "(link to follow)"}\n${COMPANY}`,
};

/**
 * Small helper for the UI: pick the right status template based on the job's
 * current status. Falls back to `documents_ready`.
 */
export const waTemplateForStatus = (status) => {
  const map = {
    scheduled: "status_scheduled",
    booked: "status_scheduled",
    en_route: "status_en_route",
    on_site: "status_on_site",
    completed: "status_completed",
    completed_pending_review: "status_completed",
    invoiced: "status_invoiced",
  };
  return map[(status || "").toLowerCase()] || "documents_ready";
};
